import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  ZfBox,
  ZfDropdownMenu,
  ZfIcon,
  ZfInput,
  ZfPill,
  ZfSearchOption,
  ZfStack,
  ZfTextButton,
  ZfToggle,
  ZfTypography,
} from '@zafire/react';
import { PartnerSearchByBrResponse, PrivateBankingServiceDefinitionId } from 'pco-product-service-internal-consumer';
import { useSearchPartnersByBr } from '../../../../../hooks/queries/privateBanking';
import { logger } from 'lib/logger';
import {
  getPartnerDescription,
  getPartnerKey,
  getPartnerTitle,
  getPartnerTypeLabel,
} from '../utils/partnerDisplayUtils';
import { SelectedPartnersGrid } from './SelectedPartnersGrid';

export type PartnerSearchWidgetProps = {
  brUid: string | undefined;
  selectedPartners: PartnerSearchByBrResponse[];
  onSelectedPartnersChange: (selectedPartners: PartnerSearchByBrResponse[]) => void;
  disabled?: boolean;
  allowAllPartnersSearch?: boolean;
  extraColumns?: ColDef<PartnerSearchByBrResponse>[];
  serviceDefinitionId?: PrivateBankingServiceDefinitionId;
  productName?: string;
  selectedPartnersContent?: React.ReactNode;
};

const ALL_PARTNERS_P2P_DEPTH = -1;
const BR_PARTNERS_P2P_DEPTH = 0;
const MIN_ALL_PARTNERS_CHARS = 3;
const NO_EXTRA_COLUMNS: ColDef<PartnerSearchByBrResponse>[] = [];

export const PartnerSearchWidget: React.FC<PartnerSearchWidgetProps> = ({
  brUid,
  selectedPartners,
  onSelectedPartnersChange,
  disabled = false,
  allowAllPartnersSearch = false,
  extraColumns = NO_EXTRA_COLUMNS,
  serviceDefinitionId,
  productName,
  selectedPartnersContent,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearchBeenRequested, setHasSearchBeenRequested] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAllPartnersMode, setIsAllPartnersMode] = useState(false);

  const p2pDepth = isAllPartnersMode ? ALL_PARTNERS_P2P_DEPTH : BR_PARTNERS_P2P_DEPTH;
  const meetsMinChars = !isAllPartnersMode || searchTerm.trim().length >= MIN_ALL_PARTNERS_CHARS;
  const showMinCharsHint = isAllPartnersMode && !meetsMinChars;
  const searchEnabled = hasSearchBeenRequested && meetsMinChars;

  useEffect(() => {
    if (brUid && hasSearchBeenRequested) {
      logger.debug('Searching eligible partners for product configuration', {
        brUid,
        serviceDefinitionId,
        searchTerm: searchTerm.trim(),
      });
    }
  }, [brUid, searchTerm, hasSearchBeenRequested, serviceDefinitionId]);

  const { data, isFetching } = useSearchPartnersByBr(brUid, searchTerm, searchEnabled, p2pDepth);
  const partners = useMemo<PartnerSearchByBrResponse[]>(() => data?.partners ?? [], [data]);
  const totalPartners = data?.total ?? partners.length;
  const selectedPartnerKeys = useMemo(() => new Set(selectedPartners.map(getPartnerKey)), [selectedPartners]);

  const handlePartnerDelete = useCallback(
    (partner: PartnerSearchByBrResponse) => {
      if (disabled) {
        return;
      }
      const partnerKey = getPartnerKey(partner);
      onSelectedPartnersChange(
        selectedPartners.filter((selectedPartner) => getPartnerKey(selectedPartner) !== partnerKey),
      );
    },
    [disabled, onSelectedPartnersChange, selectedPartners],
  );

  const selectedPartnerColumnDefs = useMemo<ColDef<PartnerSearchByBrResponse>[]>(
    () => [
      { headerName: 'Partner ID', field: 'globalPartnerId', flex: 1, filter: 'agTextColumnFilter' },
      { headerName: 'Partner name', field: 'fullName', flex: 2, sort: 'asc', filter: 'agTextColumnFilter' },
      {
        headerName: 'Type',
        flex: 1,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => getPartnerTypeLabel(data?.partnerType),
      },
      ...extraColumns,
      {
        headerName: 'Actions',
        sortable: false,
        resizable: false,
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        cellStyle: { padding: 0 },
        filter: false,
        cellRenderer: (params: ICellRendererParams<PartnerSearchByBrResponse>) => {
          const partner = params.data;
          if (!partner) return null;

          return (
            <ZfBox style={{ display: 'flex', alignItems: 'center', height: '100%', width: '80px' }}>
              <ZfTextButton
                hierarchy="secondary"
                size="extra-small"
                data-testid="selected-partner-delete-button"
                aria-label="Delete selected partner"
                disabled={disabled}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  handlePartnerDelete(partner);
                }}
              >
                Remove
              </ZfTextButton>
            </ZfBox>
          );
        },
      },
    ],
    [disabled, extraColumns, handlePartnerDelete],
  );

  const showResults = isSearchOpen && !!brUid;
  const resultCountLabel = `${totalPartners} ${totalPartners === 1 ? 'result' : 'results'}`;

  const handleInputChange = (event: CustomEvent<string>) => {
    if (disabled) {
      return;
    }
    setHasSearchBeenRequested(true);
    setIsSearchOpen(true);
    setSearchTerm(event.detail);
  };

  const handleSearchOpen = () => {
    if (disabled) {
      return;
    }
    setHasSearchBeenRequested(true);
    setIsSearchOpen(true);
  };

  const handlePartnerSelect = (partner: PartnerSearchByBrResponse) => {
    if (disabled) {
      return;
    }
    const partnerKey = getPartnerKey(partner);
    if (selectedPartnerKeys.has(partnerKey)) {
      setIsSearchOpen(false);
      return;
    }

    logger.info('Selected eligible partner', { brUid, serviceDefinitionId, partner });
    onSelectedPartnersChange([...selectedPartners, partner]);
    setIsSearchOpen(false);
  };

  return (
    <div
      data-testid="partner-search-widget"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--zf-spacing-small, 12px)',
        boxSizing: 'border-box',
      }}
    >
      <ZfStack direction="column" spacing="small" data-testid="partner-search-controls">
        <div ref={anchorRef} style={{ width: '50%' }} onPointerDownCapture={handleSearchOpen}>
          <ZfInput
            data-testid="partner-search-input"
            type="search"
            value={searchTerm}
            disabled={disabled}
            clearable={true}
            showSubmit={false}
            placeholder="Search to add eligible partners"
            onInputChange={handleInputChange}
            onClick={handleSearchOpen}
            onFocus={handleSearchOpen}
          />
        </div>

        <ZfDropdownMenu
          show={showResults}
          anchor={() => anchorRef.current as HTMLElement}
          onDropdownMenuClose={() => setIsSearchOpen(false)}
          onDropdownMenuOpen={() => setIsSearchOpen(true)}
        >
          {showResults && (
            <div
              data-testid="partner-search-results"
              style={{
                minWidth: '420px',
                border: '1px solid var(--zf-color-border-subtle, #d8dce6)',
                background: 'var(--zf-color-background-default, #fff)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
              }}
            >
              <ZfStack direction="row" spacing="small" style={{ padding: '12px 16px 8px', alignItems: 'center' }}>
                <ZfTypography variant="label-medium">PARTNER</ZfTypography>
                <ZfPill
                  size="small"
                  color="neutral"
                  variant="soft"
                  clickable={false}
                  data-testid="partner-search-result-count"
                >
                  {showMinCharsHint
                    ? `Type at least ${MIN_ALL_PARTNERS_CHARS} characters`
                    : isFetching
                      ? 'Loading...'
                      : resultCountLabel}
                </ZfPill>
                {allowAllPartnersSearch && (
                  <ZfBox style={{ marginLeft: 'auto' }}>
                    <ZfToggle
                      data-testid="partner-search-all-partners-toggle"
                      label="Search all partners"
                      checked={isAllPartnersMode}
                      disabled={disabled}
                      onToggleChange={(event: CustomEvent<boolean>) => setIsAllPartnersMode(event.detail)}
                    />
                  </ZfBox>
                )}
              </ZfStack>

              {!showMinCharsHint &&
                partners.map((partner) => (
                  <ZfSearchOption
                    data-testid="partner-search-option"
                    key={getPartnerKey(partner)}
                    titleText={getPartnerTitle(partner)}
                    description={getPartnerDescription(partner)}
                    titleHighlight={searchTerm}
                    descriptionHighlight={searchTerm}
                    onClick={() => handlePartnerSelect(partner)}
                  >
                    {selectedPartnerKeys.has(getPartnerKey(partner)) && (
                      <ZfIcon
                        data-testid="partner-search-selected-icon"
                        slot="trailing"
                        name="check_circle"
                        size="small"
                      />
                    )}
                  </ZfSearchOption>
                ))}
            </div>
          )}
        </ZfDropdownMenu>
      </ZfStack>

      {selectedPartnersContent ?? (
        <SelectedPartnersGrid
          columnDefs={selectedPartnerColumnDefs}
          selectedPartners={selectedPartners}
          productName={productName ?? '-'}
        />
      )}
    </div>
  );
};
